import { AnimatePresence, motion } from "framer-motion";
import { Package } from "lucide-react";
import { FaBoxOpen, FaClipboardList, FaTasks } from "react-icons/fa";
import { IoClose } from "react-icons/io5";
import { MdDashboard, MdKeyboardArrowDown } from "react-icons/md";
import { useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";

const StaffSidebar = ({ isSidebarOpen, setIsSidebarOpen }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [openDropdowns, setOpenDropdowns] = useState({});

  const staffMenuItems = [
    {
      label: "Dashboard",
      path: "/dashboard-staff",
      permission: "dashboard-staff",
      icon: MdDashboard,
      key: "dashboard",
    },
    {
      label: "My Jobs",
      path: "/my-jobs",
      permission: "my-jobs",
      icon: FaTasks,
      key: "my-jobs",
    },
    {
      label: "All Products",
      path: "/all-products",
      permission: "all-products",
      icon: Package,
      key: "all-products",
    },
    {
      label: "Request Stats",
      path: "/request-stats",
      permission: "request-stats",
      icon: FaClipboardList,
      key: "request-stats",
    },
  ];

  // Auto-open dropdown based on current route
  useEffect(() => {
    const newDropdownState = {};

    staffMenuItems.forEach((item) => {
      if (item.children) {
        const isChildActive = item.children.some(
          (child) =>
            location.pathname === child.path ||
            location.pathname.startsWith(child.path + "/"),
        );
        if (isChildActive) {
          newDropdownState[item.key] = true;
        }
      }
    });

    setOpenDropdowns(newDropdownState);
  }, [location.pathname]);

  const isRouteActive = (currentPath, targetPath) => {
    if (!targetPath) return false;
    if (currentPath === targetPath) return true;
    return currentPath.startsWith(targetPath + "/");
  };

  const toggleDropdown = (key) => {
    setOpenDropdowns((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const renderMenuItems = () => (
    <ul className="space-y-1">
      {staffMenuItems.map((item) => {
        const isActive = isRouteActive(location.pathname, item.path);
        const isDropdownOpen = openDropdowns[item.key];

        if (item.children?.length) {
          return (
            <li key={item.key}>
              {/* Dropdown Parent */}
              <button
                onClick={() => toggleDropdown(item.key)}
                className={`w-full flex items-center gap-2 px-3 py-2.5 my-1 rounded-xl transition-all ${
                  isActive
                    ? "bg-gradient-to-r from-brandAccentColor to-accentTextColor text-white shadow-md"
                    : "text-primaryTextColor hover:bg-gray-100"
                }`}
              >
                <span className="w-5 flex items-center justify-center">
                  <item.icon className="size-4 opacity-80" />
                </span>

                <span className="text-[0.9rem] font-medium flex-1 text-left">
                  {item.label}
                </span>

                <MdKeyboardArrowDown
                  size={18}
                  className={`transition-transform duration-200 ${
                    isDropdownOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {/* Dropdown Children */}
              <AnimatePresence>
                {isDropdownOpen && (
                  <motion.ul
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="ml-8 mt-1 space-y-1 overflow-hidden"
                  >
                    {item.children.map((child) => {
                      const isChildActive = location.pathname === child.path;

                      return (
                        <li key={child.key}>
                          <button
                            onClick={() => {
                              navigate(child.path);
                              if (window.innerWidth < 1024)
                                setIsSidebarOpen(false);
                            }}
                            className={`w-full flex items-center gap-2 text-left text-sm px-3 py-2 rounded-lg transition-all ${
                              isChildActive
                                ? "bg-accentTextColor/10 text-accentTextColor font-semibold"
                                : "text-gray-600 hover:text-accentTextColor hover:bg-gray-50"
                            }`}
                          >
                            <child.icon className="size-3 opacity-80" />
                            <span>{child.label}</span>
                          </button>
                        </li>
                      );
                    })}
                  </motion.ul>
                )}
              </AnimatePresence>
            </li>
          );
        }

        // Regular menu item
        return (
          <li key={item.key}>
            <button
              onClick={() => {
                navigate(item.path);
                if (window.innerWidth < 1024) setIsSidebarOpen(false);
              }}
              className={`w-full flex items-center gap-2 px-3 py-2.5 my-1 rounded-xl transition-all ${
                isActive
                  ? "bg-gradient-to-r from-brandAccentColor to-accentTextColor text-white shadow-md"
                  : "text-primaryTextColor hover:bg-gray-100 hover:text-accentTextColor"
              }`}
            >
              <span className="w-5 flex items-center justify-center shrink-0">
                <item.icon className="size-4 opacity-80" />
              </span>
              <span className="text-[0.9rem] font-medium truncate">
                {item.label}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <div className="hidden lg:block fixed left-0 top-0 h-screen w-[14.5rem] z-30">
        <motion.aside
          className="h-screen w-full overflow-y-auto no-scrollbar px-2 pt-6 pb-2
            bg-gradient-to-b from-white to-gray-50 border-r border-gray-200 shadow-lg"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
        >
          <div
            className="flex justify-center mb-4 cursor-pointer hover:scale-105 transition-transform"
            onClick={() => navigate("/dashboard-staff")}
          >
            <img src="/logo.png" className="h-14" alt="logo" />
          </div>

          <nav>{renderMenuItems()}</nav>
        </motion.aside>
      </div>

      {/* Mobile sidebar */}
      <div className="block lg:hidden">
        {/* Backdrop */}
        <AnimatePresence>
          {isSidebarOpen && (
            <motion.div
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsSidebarOpen(false)}
            />
          )}
        </AnimatePresence>

        {/* Sidebar Panel */}
        <AnimatePresence>
          {isSidebarOpen && (
            <motion.div
              className="fixed top-0 left-0 h-screen w-64 max-w-[85%] z-50"
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
            >
              <div className="h-screen overflow-y-auto px-4 py-6 bg-gradient-to-b from-white to-gray-50 shadow-2xl border-r border-gray-200">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                  <motion.div
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.1 }}
                  >
                    <img
                      src="/logo.png"
                      className="h-12 cursor-pointer hover:scale-105 transition-transform"
                      alt="logo"
                      onClick={() => {
                        navigate("/dashboard-staff");
                        setIsSidebarOpen(false);
                      }}
                    />
                  </motion.div>

                  <motion.button
                    onClick={() => setIsSidebarOpen(false)}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    aria-label="Close menu"
                  >
                    <IoClose size={24} className="text-gray-700" />
                  </motion.button>
                </div>

                {/* Navigation */}
                <motion.nav
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  {renderMenuItems()}
                </motion.nav>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
};

export default StaffSidebar;
